import { get, set, del } from 'idb-keyval'
import { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

const CACHE_KEY = 'react-query-offline-cache'

/**
 * While the pause depth is greater than zero, persistClient() becomes a no-op. Used during bulk prefetch
 * to prevent PersistQueryClientProvider from dehydrating + serializing the
 * entire query cache on every setQueryData call (hundreds of calls in a
 * tight loop). Without this, WKWebView OOMs because the dehydrate→serialize
 * →structured-clone pipeline creates 3-4 copies of the full cache in memory.
 *
 * Call resumePersist() after prefetch to trigger a single final persist.
 */
let persistPauseDepth = 0
let pendingClient: PersistedClient | null = null

export function pausePersist(): void {
  if (persistPauseDepth === 0) {
    // Discard only a stale snapshot from an earlier pause cycle. A nested pause must preserve the
    // current owner's pending snapshot so the outermost resume can still flush it.
    pendingClient = null
  }
  persistPauseDepth += 1
}

export async function resumePersist(): Promise<void> {
  if (persistPauseDepth === 0) return

  persistPauseDepth -= 1
  if (persistPauseDepth > 0) return

  if (pendingClient) {
    const client = pendingClient
    pendingClient = null
    await safeSet(client)
  }
}

/**
 * Probe whether a value survives the structured-clone algorithm — the same
 * algorithm IndexedDB's `put` uses internally. Non-cloneable values (functions,
 * class instances carrying methods, etc.) throw `DataCloneError`.
 *
 * The canonical offender here is an Axios object that leaked into the React
 * Query cache: an `AxiosResponse` / `InternalAxiosRequestConfig` whose
 * `headers` is an `AxiosHeaders` instance (methods `getContentType`, `isHTML`,
 * …). A query that caches a raw response instead of `response.data` poisons
 * the whole persisted snapshot. See bayalab/BUILD-LOG#211.
 */
function isStructuredCloneable(value: unknown): boolean {
  // Without the builtin we cannot answer the question — and answering it wrongly
  // is catastrophic in one direction only. structuredClone needs Chrome 98; below
  // that the CALL threw, this catch read the throw as "not cloneable", and every
  // entry was dropped. The sanitizer then deleted the entire persisted cache —
  // precisely the total loss it exists to prevent, on precisely the engine that
  // most needs an offline cache.
  //
  // That engine is not hypothetical: production telemetry found System WebView
  // pinned to Chrome/90 on an Android 11 device, because System WebView is a
  // separately Play-Store-updated APK and minSdk constrains only the OS. See
  // .browserslistrc, which records the same finding.
  //
  // So when the builtin is absent, assume cloneable and let the real IndexedDB
  // put decide. That restores the pre-sanitizer behaviour on those engines — a
  // poisoned entry can still reject the snapshot, which safeSet already handles —
  // instead of guaranteeing the loss outright.
  if (typeof structuredClone !== 'function') return true

  // The typeof guard above is exactly the check the rule asks for, and this is
  // the one call site where invoking the builtin IS the point — the function's
  // whole job is probing whether a value survives it. The rule cannot tell a
  // guarded call from an unguarded one, so it fires here too; the exemption is
  // deliberately one line wide.
  try {
    // eslint-disable-next-line no-restricted-syntax
    structuredClone(value)
    return true
  } catch {
    return false
  }
}

/**
 * Build a copy of `client` with any query (or mutation) whose state.data is
 * not structured-cloneable removed, so the remaining — well-formed — cache
 * entries still persist. Returns the sanitized client plus the dropped query
 * hashes/keys for logging.
 *
 * Without this, a single poisoned entry makes IndexedDB `put` reject the
 * ENTIRE snapshot with `DataCloneError`, so offline cold-start silently loses
 * every cached query — not just the bad one.
 */
export function sanitizePersistedClient(client: PersistedClient): {
  client: PersistedClient
  droppedQueries: string[]
  droppedMutations: number
} {
  const droppedQueries: string[] = []

  const queries = (client.clientState.queries ?? []).filter(query => {
    if (isStructuredCloneable(query)) return true
    // queryHash is the most stable identifier; fall back to the key.
    droppedQueries.push(query.queryHash ?? JSON.stringify(query.queryKey))
    return false
  })

  let droppedMutations = 0
  const mutations = (client.clientState.mutations ?? []).filter(mutation => {
    if (isStructuredCloneable(mutation)) return true
    droppedMutations += 1
    return false
  })

  return {
    client: {
      ...client,
      clientState: { ...client.clientState, queries, mutations },
    },
    droppedQueries,
    droppedMutations,
  }
}

/**
 * Persist `client` to IndexedDB, surviving non-cloneable entries.
 *
 * Happy path stays cheap — a single `set()`. Only when `set()` throws (almost
 * always `DataCloneError`) do we pay for the per-entry structured-clone probe,
 * drop the offenders, log which query keys were dropped (so the offending
 * `queryFn` can be tracked down from telemetry), and retry once with the
 * sanitized snapshot.
 */
async function safeSet(client: PersistedClient): Promise<void> {
  try {
    await set(CACHE_KEY, client)
  } catch (error) {
    const { client: sanitized, droppedQueries, droppedMutations } = sanitizePersistedClient(client)

    if (droppedQueries.length === 0 && droppedMutations === 0) {
      // The failure wasn't a non-cloneable entry (e.g. quota exceeded) — nothing
      // to sanitize, so don't loop. Surface and bail.
      console.error('[QueryPersister] Failed to persist cache:', error)
      return
    }

    console.error(
      '[QueryPersister] DataCloneError persisting cache — dropped non-serializable ' +
        `queries [${droppedQueries.join(', ')}]` +
        (droppedMutations ? ` and ${droppedMutations} mutation(s)` : '') +
        ', persisting the rest:',
      error,
    )

    try {
      await set(CACHE_KEY, sanitized)
    } catch (retryError) {
      console.error('[QueryPersister] Failed to persist sanitized cache:', retryError)
    }
  }
}

/**
 * IndexedDB persister for React Query cache.
 * Enables offline-first functionality by persisting query cache across app restarts.
 */
export const createIDBPersister = (): Persister => {
  return {
    persistClient: async (client: PersistedClient) => {
      if (persistPauseDepth > 0) {
        // Capture the latest dehydrated snapshot so resumePersist() can flush it
        pendingClient = client
        return
      }
      await safeSet(client)
    },
    restoreClient: async () => {
      try {
        return await get<PersistedClient>(CACHE_KEY)
      } catch (error) {
        console.error('[QueryPersister] Failed to restore cache:', error)
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await del(CACHE_KEY)
      } catch (error) {
        console.error('[QueryPersister] Failed to remove cache:', error)
      }
    },
  }
}

export const idbPersister = createIDBPersister()
